import { useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Formik, Field as FormikField } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';
import FormHelperText from '@mui/material/FormHelperText';
import { Button } from '../shared';
import FullScreenDialog from '../shared/fulll-screen-dialog.component';
import { agentDomain } from '../../domain';

const agentContactFormInitialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
};

const agentContactFormValidationSchema = Yup.object({
    firstName: Yup.string('').required('First name is required'),
    lastName: Yup.string('').required('Last name is required'),
    email: Yup.string('').email('Enter a valid email').required('Email is required'),
    phone: Yup.string('')
        .matches(/^[0-9,+]*$/, 'Phone number is invalid')
        .min(9, 'Phone number is too short (minimum 9)')
        .max(15, 'Phone number is too long (maximum 15)')
        .required('Phone number is required'),
    message: Yup.string('').required('Message is required'),
});

const ContactAgentDialog = (props) => {
    const { agentDetails, selectedRoomType, isOpen, setIsOpen } = props;
    const [submitError, setSubmitError] = useState(null);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState(null);
    const agentId = _.get(agentDetails, 'id');

    const inquiryMessageDefault = `Please send me more information about the ${_.get(
        selectedRoomType,
        'description',
    )}, ${_.get(selectedRoomType, 'buildingName')}.`;

    const roomTypeId = _.get(selectedRoomType, 'id');

    agentContactFormInitialValues.message = inquiryMessageDefault;
    const onSubmit = async ({ firstName, lastName, phone, email, message }, { setSubmitting }) => {
        setSubmitting(true);
        try {
            agentDomain.submitAgentInquiry(
                agentId,
                roomTypeId,
                firstName,
                lastName,
                phone,
                email,
                message,
            );
            setSubmitError(null);
            setSuccessfullySubmitted(true);
        } catch (error) {
            setSubmitError(error.message);
            setSuccessfullySubmitted(false);
        } finally {
            setSubmitting(false);
        }
    };

    const contactForm = () => {
        return (
            <>
                {!successfullySubmitted && (
                    <Formik
                        initialValues={agentContactFormInitialValues}
                        validationSchema={agentContactFormValidationSchema}
                        onSubmit={onSubmit}
                        validateOnBlur
                        validateOnChange={false}
                    >
                        {({
                            submitForm,
                            isSubmitting,
                            touched,
                            errors,
                            handleChange,
                            handleBlur,
                            handleReset,
                            handleSubmit,
                        }) => (
                            <form onReset={handleReset} onSubmit={handleSubmit}>

                                <FormikField className="contact-agent-dialog-field"
                                    component={TextField}
                                    required
                                    name="firstName"
                                    id="firstName"
                                    type="text"
                                    label="Your Name"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.firstName ? errors.firstName : ''}
                                    error={touched.firstName && !!errors.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <FormikField className="contact-agent-dialog-field"
                                    component={TextField}
                                    required
                                    name="lastName"
                                    id="lastName"
                                    type="text"
                                    label="Your Surname"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.lastName ? errors.lastName : ''}
                                    error={touched.lastName && !!errors.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <FormikField className="contact-agent-dialog-field"
                                    component={TextField}
                                    required
                                    name="email"
                                    id="email"
                                    type="email"
                                    label="Email"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.email ? errors.email : ''}
                                    error={touched.email && !!errors.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <FormikField className="contact-agent-dialog-field"
                                    component={TextField}
                                    required
                                    name="phone"
                                    id="phone"
                                    type="tel"
                                    label="Phone"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.phone ? errors.phone : ''}
                                    error={touched.phone && !!errors.phone}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <FormikField className="contact-agent-dialog-field"
                                    component={TextField}
                                    required
                                    name="message"
                                    id="message"
                                    type="text"
                                    label="Message"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.message ? errors.message : ''}
                                    error={touched.message && !!errors.message}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <br />

                                <FormHelperText error>{submitError}</FormHelperText>

                                <Button className="contact-agent-dialog-button"
                                    variant="contained"
                                    color="primary"
                                    isLoading={isSubmitting}
                                    onClick={submitForm}
                                    id="sign-up-form"
                                    type="submit"
                                >
                                    Send Message
                                </Button>

                            </form>
                        )}
                    </Formik>
                )}
                {successfullySubmitted && (
                    <div className='contact-agent-dialog-thank-you-message-container'>
                        <h3>Thanks for Getting in Touch!</h3>
                        <p>Your inquiry has been sent and the agent should contact you ASAP</p>
                    </div>
                )}
            </>
        );
    };

    return (
        <FullScreenDialog isOpen={isOpen} setIsOpen={setIsOpen}>
            <div className='container-root container-root-maxWidthLg'>
                <h4 className='contact-agent-dialog-header4'>Contact Agent</h4>
                {contactForm()}
            </div>
        </FullScreenDialog>
    );
};

export default ContactAgentDialog;

ContactAgentDialog.propTypes = {
    selectedRoomType: PropTypes.object,
    agentDetails: PropTypes.object,
    isOpen: PropTypes.bool,

    setIsOpen: PropTypes.func,
};
